import { readProductCurrency } from '@/domain/product/currency'
import { format, isValid, parse, parseISO, startOfDay } from 'date-fns'
import { fr } from 'date-fns/locale'
import { StackIcon } from '@phosphor-icons/react/dist/ssr/Stack'
import type {
  CellClassParams,
  Column,
  EditableCallbackParams,
  GridApi,
  IAggFuncParams,
  ICellEditorParams,
  ICellRendererParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueParserParams,
  ValueSetterParams,
} from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { cx } from 'class-variance-authority'
import { uniq } from 'lodash'
import type { Product } from '@skop-app/lib-domain/types'
import { formatPrice } from '@skop-app/lib-domain/utils'

import {
  defaultValueGetter,
  defaultValueSetter,
} from '@/components/Grid/utils/grid.utils'
import { formatPropertyNumericDisplayValue } from '@/services/property/property-display'
import { formulaOverride } from '@/utils/formula.helpers'
import {
  FORMULA_DISPLAY_PLACEHOLDER,
  isFormulaInvalidMarker,
} from '@/utils/formula.evaluation'
import { isSupportedNumericString, parseNumber } from '@/utils/parsers'
import {
  getReviewColId,
  getReviewGridProjectionFromContext,
  isDisplayablePendingReviewThread,
  resolveReviewColumnId,
} from '@/utils/reviews'
import type { AdaptedProduct, Property } from '@/types'
import type { GridContext } from '@/types/grid'
import {
  EmptyCellRenderer,
  FolderCellRenderer,
  ImageCellRenderer,
  ReviewPillRenderer,
} from '@/components/atoms/AGGrid/renderers'
import {
  SelectCreateEditor,
  SelectPlaceEditor,
} from '@/components/Grid/editors'
import {
  resolveBatchGroupLabel,
  resolveBatchGroupPictureValue,
} from './groupDisplay'
import { formatRefDisplayValue } from './refDataDisplay'

type RowWithId = { id?: string }

const isNewLine = (params: ICellRendererParams): boolean => {
  const data = params.data as RowWithId | undefined
  return Boolean(data?.id?.startsWith('new'))
}

const dateFormats = ['yyyy-MM-dd', 'MM/dd/yyyy', 'dd/MM/yyyy']

export const groupCellRenderer = () => {
  return {
    innerRenderer: (params: CustomCellRendererProps) => {
      const { valueFormatted, value, colDef, context } = params
      const { allLeafChildren, allChildrenCount, level, key } = params.node

      type GroupItem = { label?: string; value?: string; type?: string }
      type LeafData = { groups?: GroupItem[] }

      const firstLeaf = allLeafChildren?.[0]
      const firstLeafGroups = (firstLeaf?.data as LeafData | undefined)?.groups
      const groupAtLevel = Array.isArray(firstLeafGroups)
        ? (firstLeafGroups[level] ?? firstLeafGroups[0])
        : undefined
      const nodeLabel =
        typeof value === 'string'
          ? value
          : (valueFormatted ??
            groupAtLevel?.label ??
            groupAtLevel?.value ??
            (key ? String(key) : ''))

      if (allChildrenCount) {
        const isBatch =
          (allLeafChildren ?? []).some((leaf) => {
            const groups = (leaf.data as LeafData | undefined)?.groups
            return (groups ?? []).some((g) => g?.type === 'occurrence')
          }) ?? false

        if (isBatch) {
          const primaryLabel = resolveBatchGroupLabel({
            context: context as GridContext | undefined,
            firstLeafData: firstLeaf?.data,
            fallbackLabel: nodeLabel || 'Occurrences',
          })

          if (colDef?.field === 'pictures') {
            return (
              <ImageCellRenderer
                {...params}
                data={firstLeaf?.data}
                value={resolveBatchGroupPictureValue({
                  aggregatedValue: value,
                  firstLeafData: firstLeaf?.data,
                })}
              />
            )
          }

          return (
            <div
              className="flex shrink-0 items-center space-x-2"
              title={primaryLabel}
            >
              <StackIcon
                className="size-4 shrink-0 text-slate-300"
                weight="fill"
              />
              <span>{primaryLabel}</span>
            </div>
          )
        }

        return <FolderCellRenderer {...params} />
      }
      if (params.colDef?.field === 'reviews') {
        return <ReviewPillRenderer {...params} />
      }
      if (typeof valueFormatted === 'string' && valueFormatted.length > 0) {
        return valueFormatted
      }
      if (typeof value === 'string') return value
      if (value && typeof value === 'object' && 'label' in value) {
        const v = value as { label?: unknown }
        return typeof v.label === 'string' ? v.label : ''
      }
      return ''
    },
  }
}

export const cellRendererSelector = (params: ICellRendererParams) => {
  const firstUserColumn = getFirstUserColumn(params.api)

  if (
    firstUserColumn?.getColId() === params.column?.getColId() &&
    !params.value &&
    isNewLine(params)
  ) {
    return {
      component: EmptyCellRenderer,
    }
  }

  if (
    params.node?.group &&
    displayOnFirstColumn(params) &&
    !isNewLine(params)
  ) {
    return {
      component: 'agGroupCellRenderer',
      params: groupCellRenderer(),
      indentSize: 24,
    }
  }
}

function getFirstUserColumn(api: GridApi): Column | undefined {
  const displayedColumns = api.getAllDisplayedColumns() || []
  return displayedColumns.find((col) => !col.getColId().startsWith('ag-Grid-'))
}

const displayOnFirstColumn = (params: ICellRendererParams): boolean => {
  const firstUserColumn = getFirstUserColumn(params.api)
  return Boolean(
    firstUserColumn &&
    params.column &&
    firstUserColumn.getColId() === params.column.getColId(),
  )
}

export const dynamicCellEditorSelector = (
  params: ICellEditorParams<AdaptedProduct>,
) => {
  const field = params.colDef?.field
  if (field === 'placeID')
    return {
      component: SelectPlaceEditor,
    }
  return {
    component: SelectCreateEditor,
  }
}

export const defaultNumberValueFormatter = (
  params: ValueFormatterParams<AdaptedProduct>,
) => {
  const { colDef, value } = params
  const field = colDef?.field as string
  if (!field) return ''

  if (value === null || value === undefined || value === '') return ''

  const numericValue =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(',', '.').trim())

  if (Number.isNaN(numericValue)) return ''

  if (field.includes('price')) {
    return formatPrice({
      amount: numericValue,
      currency: readProductCurrency(
        (params.data ?? params.node?.allLeafChildren?.[0]?.data) as
          | Record<string, unknown>
          | undefined,
      ),
    })
  }

  if (field === 'weight') {
    return `${numericValue.toLocaleString('fr-FR')} kg`
  }

  return numericValue.toLocaleString('fr-FR')
}

/** Returns unwrapped value for display/editing so override template =(x) shows as x. */
export const formulaValueGetter = (
  params: Parameters<typeof defaultValueGetter>[0],
) => {
  const raw = defaultValueGetter(params)
  const str =
    typeof raw === 'string'
      ? raw
      : raw && typeof raw === 'object' && 'value' in raw
        ? String((raw as { value: unknown }).value)
        : undefined
  if (str === undefined || str === null) return raw
  return formulaOverride(str) ?? str
}

const toEffectiveValue = (v: unknown): string =>
  String(formulaOverride(String(v ?? '')) ?? v ?? '')

export const formulaValueParser = (
  params: ValueParserParams<AdaptedProduct>,
) => {
  const oldValue = params.oldValue?.value
  const newValue = params.newValue
  if (oldValue === newValue) return newValue
  if (
    !newValue ||
    (oldValue && formulaOverride(oldValue) && !formulaOverride(newValue))
  ) {
    return newValue
  }
  return formulaOverride(newValue) ? newValue : `=(${newValue})`
}

/** No-op when effective value unchanged (click + blur without edit). Prevents phantom events. */
export const formulaValueSetter = (
  params: ValueSetterParams<AdaptedProduct, string>,
) => {
  const { oldValue, newValue } = params
  if (toEffectiveValue(oldValue) === toEffectiveValue(newValue)) return false
  return defaultValueSetter(params)
}

export const formatFormulaDisplayValue = ({
  rowId,
  value,
  property,
}: {
  rowId?: string
  value: unknown
  property?: Pick<Property, 'format' | 'suffix' | 'autoWeightUnit'>
}): string => {
  if (rowId?.startsWith('new')) return ''
  if (
    value === FORMULA_DISPLAY_PLACEHOLDER ||
    value === null ||
    value === undefined ||
    isFormulaInvalidMarker(value)
  ) {
    return FORMULA_DISPLAY_PLACEHOLDER
  }

  const finalValue =
    formulaOverride(typeof value === 'string' ? value : String(value ?? '')) ??
    value
  if (
    finalValue == null ||
    finalValue === '' ||
    finalValue === FORMULA_DISPLAY_PLACEHOLDER ||
    isFormulaInvalidMarker(finalValue)
  ) {
    return FORMULA_DISPLAY_PLACEHOLDER
  }

  const numericValue =
    typeof finalValue === 'number'
      ? finalValue
      : isSupportedNumericString(finalValue)
        ? parseNumber(finalValue)
        : null

  if (property?.autoWeightUnit) {
    if (numericValue === null || !Number.isFinite(numericValue)) {
      return FORMULA_DISPLAY_PLACEHOLDER
    }
    return formatPropertyNumericDisplayValue({
      value: numericValue,
      locale: 'fr-FR',
      suffix: property.suffix,
      autoWeightUnit: property.autoWeightUnit,
    })
  }

  if (property?.format === 'integer' || property?.format === 'float') {
    if (numericValue === null || !Number.isFinite(numericValue)) {
      return FORMULA_DISPLAY_PLACEHOLDER
    }
    return formatPropertyNumericDisplayValue({
      value: numericValue,
      locale: 'fr-FR',
      suffix: property.suffix,
      autoWeightUnit: property.autoWeightUnit,
    })
  }

  return property?.suffix
    ? `${finalValue} ${property.suffix}`
    : String(finalValue)
}

export const formulaValueFormatter = (
  params: ValueFormatterParams<AdaptedProduct>,
) => {
  const { data, value, colDef } = params
  return formatFormulaDisplayValue({
    rowId: data?.id,
    value,
    property: colDef?.cellEditorParams?.property,
  })
}

export const arrayValuesParser = (params: ValueParserParams<Product>) => {
  if (Array.isArray(params.newValue)) {
    params.newValue = params.newValue.join(',')
  }
  return params.newValue
}

export const suppressKeyboardEvent = (params: SuppressKeyboardEventParams) => {
  const editing = params.api.getEditingCells()[0]
  return editing && params.event.key === 'Enter'
}

export const dateFilterParams = {
  includeTime: false,
  inRangeInclusive: true,
  comparator: (filterLocalDateAtMidnight: Date, cellValue: unknown) => {
    let cellDate: Date | null = null

    if (cellValue instanceof Date) {
      cellDate = isValid(cellValue) ? startOfDay(cellValue) : null
    } else if (typeof cellValue === 'string') {
      const parsedIso = parseISO(cellValue)
      if (isValid(parsedIso)) {
        cellDate = startOfDay(parsedIso)
      } else {
        for (const formatString of dateFormats) {
          const parsed = parse(cellValue, formatString, new Date())
          if (isValid(parsed)) {
            cellDate = startOfDay(parsed)
            break
          }
        }
      }
    }

    if (!cellDate) return -1
    if (cellDate < filterLocalDateAtMidnight) return -1
    if (cellDate > filterLocalDateAtMidnight) return 1
    return 0
  },
}

export const dateCellFormatter = ({
  value,
}: ValueFormatterParams | { value: string }) => {
  if (!value) return ''

  try {
    let parsedDate: Date | null = null

    if (typeof value === 'string') {
      parsedDate = parseISO(value)

      if (!isValid(parsedDate)) {
        for (const formatString of dateFormats) {
          const parsed = parse(value, formatString, new Date())
          if (isValid(parsed)) {
            parsedDate = parsed
            break
          }
        }
      }
    } else if (value instanceof Date) {
      parsedDate = value
    }

    if (parsedDate && isValid(parsedDate)) {
      return format(parsedDate, 'dd/MM/yyyy', { locale: fr })
    }

    return ''
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

export const defaultNumberComparator = (
  valueA: number | string | null | undefined,
  valueB: number | string | null | undefined,
) => {
  const aIsEmpty = valueA === null || valueA === undefined || valueA === ''
  const bIsEmpty = valueB === null || valueB === undefined || valueB === ''
  if (aIsEmpty && bIsEmpty) return 0
  if (aIsEmpty) return -1
  if (bIsEmpty) return 1

  const aValue =
    typeof valueA === 'number' ? valueA : parseFloat(String(valueA))
  const bValue =
    typeof valueB === 'number' ? valueB : parseFloat(String(valueB))
  if (Number.isNaN(aValue) && Number.isNaN(bValue)) return 0
  if (Number.isNaN(aValue)) return -1
  if (Number.isNaN(bValue)) return 1

  return aValue - bValue
}

export const sumFunction = (params: IAggFuncParams<AdaptedProduct>) => {
  const { values } = params
  return values.reduce((acc, value) => acc + (value ? parseFloat(value) : 0), 0)
}

export const joinFunction = (
  params: IAggFuncParams<AdaptedProduct>,
  refDataByKeyStoreId?: string,
) => {
  try {
    const validValues = params.values
      .flatMap((value) => {
        if (value && typeof value === 'object' && Array.isArray(value.labels)) {
          return value.labels.filter(
            (label: unknown): label is string => typeof label === 'string',
          )
        }
        if (value && typeof value === 'object' && 'label' in value) {
          return [String(value.label)]
        }
        if (typeof value !== 'string') return []
        return [
          formatRefDisplayValue({
            value,
            field: params.colDef?.field ?? '',
            fieldType: params.colDef?.cellEditorParams?.property?.type,
            refData: params.colDef?.refData,
            context: params.context,
            refDataByKeyStoreId,
          }),
        ]
      })
      .filter(Boolean)

    const labels = uniq(validValues).sort((a, b) => a.localeCompare(b))
    const result = labels.join(', ')

    return {
      label: result,
      value: result,
      labels,
    }
  } catch (error) {
    console.error('Error in join function:', error)
    return {
      label: '',
      value: '',
      labels: [],
    }
  }
}

export const joinActionFunction = (params: IAggFuncParams<AdaptedProduct>) => {
  return params.values
}

type CellEditableRule = (params: CellClassParams) => boolean

const getRowEditableRule = (context: unknown): CellEditableRule | null => {
  if (!context || typeof context !== 'object') return null
  const record = context as Record<string, unknown>
  const rawRules = record.__gridRules
  if (!rawRules || typeof rawRules !== 'object') return null
  const rulesRecord = rawRules as Record<string, unknown>
  const rawIsRowEditable = rulesRecord.isRowEditable
  if (typeof rawIsRowEditable !== 'function') return null
  return rawIsRowEditable as CellEditableRule
}

const isCellEditable = (params: CellClassParams): boolean => {
  const editable = params.colDef.editable
  if (typeof editable === 'boolean') return editable
  if (typeof editable === 'function') {
    return editable(params as unknown as EditableCallbackParams)
  }
  return true
}

export const handleCellClass = (
  params: CellClassParams,
  className?: Record<string, boolean>,
) => {
  const { data, context, node, colDef, value } = params
  const rowEditableRule = getRowEditableRule(context)
  const rowDisabled = rowEditableRule ? !rowEditableRule(params) : false
  const cellDisabled = rowDisabled || !isCellEditable(params)
  const reviewsById =
    getReviewGridProjectionFromContext(context).reviewsByElementId
  const reviewColumnId = resolveReviewColumnId({
    colDef,
    column: params.column,
  })
  const hasPendingReviewOnCell =
    Boolean(node.data?.id) &&
    Boolean(reviewColumnId) &&
    (reviewsById?.[node.data?.id]?.some(
      (review) =>
        isDisplayablePendingReviewThread(review) &&
        getReviewColId(review) === reviewColumnId,
    ) ??
      false)

  return cx({
    'font-italic text-slate-200': data?.id?.startsWith('new'),
    'text-slate-300': cellDisabled,
    'underline decoration-yellow-500 underline-offset-2 decoration-dashed':
      value && formulaOverride(value),
    'ag-cell-with-review': hasPendingReviewOnCell,
    ...className,
  })
}

export const textCaseInsensitiveComparator = (
  valueA: string,
  valueB: string,
) => {
  if (!valueA) return -1
  if (!valueB) return 1
  if (!valueA && !valueB) return 0
  const aValue = formulaOverride(valueA) || valueA
  const bValue = formulaOverride(valueB) || valueB
  const strA = typeof aValue === 'string' ? aValue.toLowerCase() : ''
  const strB = typeof bValue === 'string' ? bValue.toLowerCase() : ''
  return strA.localeCompare(strB)
}

const parseValue = (value: string) => value && String(value).toLocaleLowerCase()

export const arrayComparator = (valueA: Property[], valueB: Property[]) => {
  const AString = Array.isArray(valueA) ? valueA[0]?.label : valueA
  const BString = Array.isArray(valueB) ? valueB[0]?.label : valueB
  const A = parseValue(AString)
  const B = parseValue(BString)
  if (!A) return -1
  if (!B) return 1
  if (!A && !B) return 0
  return A.localeCompare(B)
}

export const textComparator = (valueA: string, valueB: string) => {
  if (!valueA?.length) return -1
  if (!valueB?.length) return 1
  if (!valueA?.length && !valueB?.length) return 0
  const strA = valueA.toLowerCase()
  const strB = valueB.toLowerCase()
  return strA.localeCompare(strB)
}
